import { NavLink } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
}

export default function Sidebar({ isOpen, onClose, collapsed, onToggleCollapse }: SidebarProps) {
  const { profile, signOut } = useAuth();
  const isAdmin = profile?.rol === 'Yönetici';
  const isOffice = profile?.rol === 'Ofis Personeli';

  const menuItems = [
    { path: '/', label: 'Ana Sayfa', icon: 'ri-dashboard-line', exact: true },
    { path: '/musteriler', label: 'Müşteriler', icon: 'ri-user-3-line' },
    { path: '/servis-kayitlari', label: 'Servis Kayıtları', icon: 'ri-tools-line' },
    { path: '/servis-planlama', label: 'Servis Planlama', icon: 'ri-calendar-line' },
    { path: '/bakim-takip', label: 'Bakım Takibi', icon: 'ri-shield-check-line' },
    { path: '/mesaj-gonder', label: 'Mesaj İşlemleri', icon: 'ri-message-3-line' },
    { path: '/raporlar', label: 'Raporlar', icon: 'ri-bar-chart-2-line', adminOnly: true },
    { path: '/personel', label: 'Personeller', icon: 'ri-team-line' },
    { path: '/stok', label: 'Stok Yönetimi', icon: 'ri-store-2-line' },
    { path: '/firma-ayarlari', label: 'Firma Ayarları', icon: 'ri-settings-3-line', adminOnly: true },
    { path: '/islem-logla', label: 'İşlem Logları', icon: 'ri-file-list-3-line', adminOnly: true },
  ];

  const visibleItems = menuItems.filter((item) => {
    if (item.adminOnly && !isAdmin) return false;
    return true;
  });

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-30 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full bg-white border-r border-gray-100 z-40 flex flex-col transition-all duration-300
          ${isOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0
          ${collapsed ? 'w-20' : 'w-64'}`}
      >
        {/* Logo */}
        <div className={`flex items-center gap-3 px-5 py-4 border-b border-gray-100 bg-sky-600 ${collapsed ? 'justify-center px-2' : ''}`}>
          {!collapsed && (
            <>
              <img
                src="https://static.readdy.ai/image/63ce3e67fd2d5effcfb662b2683f46ed/027b0604dcb426068ceb28ac7e1b2bcd.jpeg"
                alt="Sadem Logo"
                className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
              />
              <div className="min-w-0">
                <div className="text-white font-bold text-sm leading-tight truncate">Sadem Su Arıtma</div>
                <div className="text-sky-200 text-xs">Servis Yönetimi</div>
              </div>
            </>
          )}
          {collapsed && (
            <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
              <i className="ri-drop-line text-white text-xl"></i>
            </div>
          )}
          <button
            onClick={onClose}
            className={`text-sky-200 hover:text-white lg:hidden cursor-pointer ${collapsed ? 'ml-0' : 'ml-auto'}`}
          >
            <i className="ri-close-line text-xl"></i>
          </button>
          {!collapsed && (
            <button
              onClick={onToggleCollapse}
              className="hidden lg:flex text-sky-200 hover:text-white cursor-pointer ml-auto"
              title="Menüyü Daralt"
            >
              <i className="ri-arrow-left-s-line text-lg"></i>
            </button>
          )}
          {collapsed && (
            <button
              onClick={onToggleCollapse}
              className="hidden lg:flex text-sky-200 hover:text-white cursor-pointer"
              title="Menüyü Aç"
            >
              <i className="ri-arrow-right-s-line text-lg"></i>
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-3">
          {visibleItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.exact}
              onClick={onClose}
              className={({ isActive }) =>
                `flex items-center gap-3 py-3 text-sm font-medium transition-all duration-150 cursor-pointer
                ${collapsed ? 'px-2 justify-center' : 'px-5'}
                ${isActive
                  ? 'bg-sky-50 text-sky-600 border-l-4 border-sky-500'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 border-l-4 border-transparent'
                }`
              }
              title={collapsed ? item.label : undefined}
            >
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className={`${item.icon} text-lg`}></i>
              </div>
              {!collapsed && <span>{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* User info */}
        <div className={`border-t border-gray-100 bg-gray-50 ${collapsed ? 'px-2 py-3' : 'px-5 py-4'}`}>
          {collapsed ? (
            <div className="flex flex-col items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-user-line text-sky-600 text-sm"></i>
              </div>
              <button onClick={signOut} className="text-gray-400 hover:text-red-500 cursor-pointer" title="Çıkış Yap">
                <i className="ri-logout-box-line text-sm"></i>
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-user-line text-sky-600 text-sm"></i>
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-gray-800 truncate">
                  {profile ? `${profile.ad} ${profile.soyad}` : 'Yönetici'}
                </div>
                <div className="text-xs text-gray-500">{profile?.rol || 'Yönetici'}</div>
              </div>
              <button onClick={signOut} className="text-gray-400 hover:text-red-500 cursor-pointer" title="Çıkış Yap">
                <i className="ri-logout-box-line text-lg"></i>
              </button>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}